<template>
  <v-dialog
    v-model="show"
    persistent
    max-width="700px"
    scrollable
    transition="dialog-transition"
  >
    <v-card>
      <v-card-title class="grey lighten-3">
        <v-layout align-center>
          <v-icon class="draggable">mdi-drag</v-icon>
          <v-flex v-if="gotTemplate">{{ templateLabel }}</v-flex>
          <v-flex v-else pt-5>
            <v-row>
              <v-col class="py-0">
                <destination-list-select
                  v-model="destinationIdFilter"
                  :outlined="outlinedInputs"
                  hide-details
                  dense
                ></destination-list-select>
              </v-col>
              <v-col class="py-0">
                <template-type-list-select
                  v-model="templateTypeIds"
                  :outlined="outlinedInputs"
                  hide-details
                  dense
                  multiple
                ></template-type-list-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <template-list-select
                  @onSelect="onTemplateSelect"
                  :outlined="outlinedInputs"
                  :filter="templateFilters"
                  :include-external="true"
                ></template-list-select>
              </v-col>
            </v-row>
          </v-flex>
        </v-layout>
      </v-card-title>
      <v-card-text class="py-3">
        <v-card class="pa-3">
          <v-container grid-list-md fluid wrap pa-0>
            <v-layout row wrap v-if="!hideStartDate" justify-center>
              <v-flex xs12 sm4>
                <date-picker
                  customLabel="Start Date"
                  @onDateSelected="onStartDateSelect($event)"
                  :rangeStart="dateRangeStart"
                  :rangeEnd="dateRangeEnd"
                  :gt-date-string="startDate"
                  :outlined="outlinedInputs"
                  :allowedDatesFn="departureDates"
                  :disabled="!gotTemplate"
                ></date-picker>
              </v-flex>
              <v-flex> </v-flex>
            </v-layout>

            <v-layout
              row
              wrap
              v-for="roomType in accommodationSettings"
              :key="roomType.id"
            >
              <v-flex xs6>
                <v-text-field
                  type="number"
                  :label="roomType.label"
                  v-model="roomType.qty"
                  :min="roomType.min"
                  :outlined="outlinedInputs"
                  :disabled="$apollo.loading"
                ></v-text-field>
              </v-flex>
              <v-flex xs2>
                <v-text-field
                  type="number"
                  label="Adults"
                  v-model="roomType.adults"
                  @input="onPaxChange(roomType)"
                  min="0"
                  :max="maxAdults"
                  :outlined="outlinedInputs"
                  :disabled="$apollo.loading"
                ></v-text-field>
              </v-flex>
              <v-flex xs2>
                <v-text-field
                  type="number"
                  label="Children"
                  v-model="roomType.children"
                  @input="onPaxChange(roomType)"
                  min="0"
                  :max="maxChildren"
                  :outlined="outlinedInputs"
                  :disabled="$apollo.loading"
                ></v-text-field>
              </v-flex>
              <v-flex xs2>
                <v-text-field
                  type="number"
                  label="Infants"
                  v-model="roomType.infants"
                  min="0"
                  :max="maxInfants"
                  :outlined="outlinedInputs"
                  :disabled="$apollo.loading"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
        <v-layout row wrap pt-3>
          <v-flex xs6 text-right pr-3>
            <h3>Total:</h3>
          </v-flex>
          <v-flex xs2>{{ totals.adults }}</v-flex>
          <v-flex xs2>{{ totals.children }}</v-flex>
          <v-flex xs2>{{ totals.infants }}</v-flex>
        </v-layout>
        <v-container
          v-for="otherType in otherTypesSettings"
          :key="otherType.id"
          class="pb-0 mb-0"
        >
          <h6 class="subtitle-2">{{ otherType.label }}:</h6>
          <v-row>
            <v-col>
              <v-text-field
                type="number"
                label="Adults"
                v-model.number="otherType.adults"
                min="0"
                :outlined="outlinedInputs"
                hide-details
                :disabled="$apollo.loading"
              ></v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                type="number"
                label="Children"
                v-model.number="otherType.children"
                min="0"
                :outlined="outlinedInputs"
                hide-details
                :disabled="$apollo.loading"
              ></v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                type="number"
                label="Infants"
                v-model.number="otherType.infants"
                min="0"
                :outlined="outlinedInputs"
                hide-details
                :disabled="$apollo.loading"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-container>
        <v-container v-if="useMaxes">
          <v-row>
            <v-col xs4><strong>Max Adults:</strong> {{ maxes.adults }}</v-col>
            <v-col xs4
              ><strong>Max Children:</strong> {{ maxes.children }}</v-col
            >
            <v-col xs4><strong>Max Infants:</strong> {{ maxes.infants }}</v-col>
          </v-row>
        </v-container>
        <v-card class="mt-1" v-if="createItinerary">
          <v-container>
            <participants-list
              ref="participantsList"
              :participants="participants"
              @add="onAddParticipant"
              @addPaxDialogClosed="pendingCreateItinerary = false"
              @replace="onReplaceParticipant"
              @remove="onRemoveParticipant"
              hideEditClient
              :disabled="$apollo.loading"
              :loading="$apollo.loading"
            ></participants-list>
          </v-container>
        </v-card>
        <v-container fluid px-0 pb-0>
          <v-alert type="info" outlined>
            Some services will be priced as <strong>0</strong> if there is no
            valid rates.
          </v-alert>
        </v-container>
        <div v-if="debug">
          <code>{{ maxes }}</code>
          <code>{{ otherTypesSettings }}</code>
          <code>{{ useMaxes }}</code>
        </div>
      </v-card-text>

      <v-card-actions class="grey lighten-4">
        <v-spacer></v-spacer>
        <v-btn color="warning" text @click="onCancel">Cancel</v-btn>
        <v-btn
          v-if="createItinerary"
          color="primary"
          @click="onCreateItinerary"
          :disabled="!gotTemplate || startDate == ''"
          >Create Itinerary</v-btn
        >
        <v-btn
          v-else
          color="primary"
          @click="onAdd"
          :disabled="!gotTemplate || startDate == '' || !somethingSelected"
          >Add Template</v-btn
        >
      </v-card-actions>
    </v-card>
    <v-dialog v-model="showConfirmationDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">Confirmation</v-card-title>
        <v-card-text>
          This will expand your itinerary date range. Are you sure you want to
          continue?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            color="red darken-1"
            text
            @click="showConfirmationDialog = false"
            >No</v-btn
          >

          <v-btn color="green darken-1" text @click="onConfirm">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { TEMPLATE_DETAILS_QUERY } from "../../../constants/graphql";
import DatePicker from "../shared/DatePicker";
import TemplateListSelect from "../shared/TemplateListSelect";
import ParticipantsList from "../itineraries/builder/ItineraryBuilderParticipantsList2";
import { sortBy } from "../../../helpers";
import DestinationListSelect from "../shared/DestinationListSelect.vue";
import TemplateTypeListSelect from "../shared/TemplateTypeListSelect.vue";
var randomize = require("randomatic");
var moment = require("moment");

export default {
  name: "TemplateSettingsDialog",

  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
    selectedTemplate: {
      type: Object,
      default: () => null,
    },
    dateRangeStart: {
      type: String,
      default: () => "",
    },
    dateRangeEnd: {
      type: String,
      default: () => "",
    },
    maxAdults: {
      type: String,
    },
    maxChildren: {
      type: String,
    },
    maxInfants: {
      type: String,
    },
    initialStartDate: {
      type: String,
      default: () => "",
    },
    createItinerary: {
      type: Boolean,
      default: () => false,
    },
    hideStartDate: {
      type: Boolean,
      default: () => false,
    },
    includeSettings: {
      type: Boolean,
      default: () => false,
    },
    destinationId: {
      type: String,
      default: () => "",
    },
  },

  components: {
    DatePicker,
    TemplateListSelect,
    ParticipantsList,
    DestinationListSelect,
    TemplateTypeListSelect,
  },

  data() {
    return {
      debug: false,
      pendingCreateItinerary: false,
      template: this.selectedTemplate ? this.selectedTemplate : {},
      templateDetails: {},
      destinationIdFilter: this.destinationId,
      templateTypeIds: null,
      startDate: "",
      // this.initialStartDate != ""
      //   ? this.initialStartDate
      //   : this.dateRangeStart,
      //it gets overwritten on details load
      roomTypes: [
        {
          id: randomize("Aa0", 32),
          label: "Single Occupancy Rooms",
          occupancy: 1,
          min: 0,
          qty: "",
          adults: 0,
          children: 0,
          infants: 0,
        },
        {
          id: randomize("Aa0", 32),
          label: "Twin/Double Rooms",
          occupancy: 2,
          min: 0,
          qty: "",
          adults: 0,
          children: 0,
          infants: 0,
        },
        {
          id: randomize("Aa0", 32),
          label: "Triple Rooms",
          occupancy: 3,
          min: 0,
          qty: "",
          adults: 0,
          children: 0,
          infants: 0,
        },
        {
          id: randomize("Aa0", 32),
          label: "Quadruple(4+) Rooms",
          occupancy: 4,
          min: 0,
          qty: "",
          adults: 0,
          children: 0,
          infants: 0,
        },
      ],
      otherTypes: [
        {
          id: randomize("Aa0", 32),
          label: "TRANSPORT",
          adults: 0,
          children: 0,
          infants: 0,
          type: "TRANSPORT",
        },
        {
          id: randomize("Aa0", 32),
          label: "ACTIVITY",
          adults: 0,
          children: 0,
          infants: 0,
          type: "ACTIVITY",
        },
        {
          id: randomize("Aa0", 32),
          label: "TRAVEL",
          adults: 0,
          children: 0,
          infants: 0,
          type: "TRAVEL",
        },
        {
          id: randomize("Aa0", 32),
          label: "OTHER",
          adults: 0,
          children: 0,
          infants: 0,
          type: "OTHER",
        },
        {
          id: randomize("Aa0", 32),
          label: "PACKAGE",
          adults: 0,
          children: 0,
          infants: 0,
          type: "PACKAGE",
        },
      ],
      showConfirmationDialog: false,
      participants: [],
      serviceTypeBased: false,
    };
  },

  computed: {
    useMaxes() {
      return this.roomTypes.length == 0;
    },
    templateFilters() {
      const filters = {};
      if (this.destinationIdFilter && this.destinationIdFilter != "") {
        filters.destinationId = this.destinationIdFilter;
      }
      if (this.templateTypeIds && this.templateTypeIds.length > 0) {
        filters.templateTypes_eq = [...this.templateTypeIds];
      }
      return filters;
    },
    accommodationSettings() {
      const accommodationServices =
        this.templateDetails && this.templateDetails.items
          ? this.templateDetails.items.edges
              .filter(
                (item) => item.node.supplier.serviceType == "ACCOMMODATION"
              )
              .map((item) =>
                this.serviceTypeBased
                  ? item.node.service.typeName
                  : Number(item.node.service.stdOccupancy)
              )
          : [];

      return [
        ...this.roomTypes.filter((rt) =>
          this.serviceTypeBased
            ? accommodationServices.indexOf(rt.type) > -1
            : accommodationServices.indexOf(rt.occupancy) > -1
        ),
      ];
    },
    otherTypesSettings() {
      const otherServicesTypes =
        this.templateDetails && this.templateDetails.items
          ? [
              ...new Set(
                this.templateDetails.items.edges
                  .filter(
                    (item) => item.node.supplier.serviceType != "ACCOMMODATION"
                  )
                  .map((item) => item.node.supplier.serviceType)
              ),
            ]
          : [];
      return this.otherTypes.filter(
        (ot) => otherServicesTypes.indexOf(ot.type) > -1
      );
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    agentId() {
      return this.$store.getters.itineraryAgentId;
    },
    department() {
      return this.$store.getters.itineraryDepartment;
    },
    templateLabel() {
      //console.log(this.template);
      return this.templateDetails && this.templateDetails.hasOwnProperty("name")
        ? `${this.templateDetails.name} / ${this.templateDetails.destination.name} - Settings`
        : "Add Template Settings";
    },
    somethingSelected() {
      return (
        this.totals.adults > 0 ||
        this.totals.children > 0 ||
        this.totals.infants > 0 ||
        this.otherTotal.adults > 0 ||
        this.otherTotal.children > 0 ||
        this.otherTotal.infants > 0
      );
    },
    gotTemplate() {
      return this.template.hasOwnProperty("id");
    },

    roomTotals() {
      return this.roomTypes.reduce(
        (t, roomType) => {
          t.adults = Number(t.adults) + Number(roomType.adults);
          t.children = Number(t.children) + Number(roomType.children);
          t.infants = Number(t.infants) + Number(roomType.infants);
          return t;
        },
        { adults: 0, children: 0, infants: 0 }
      );
    },
    maxes() {
      const max = {
        adults: 0,
        children: 0,
        infants: 0,
      };
      for (const otherType of this.otherTypesSettings) {
        for (const paxType of Object.keys(max)) {
          if (otherType[paxType] > max[paxType]) {
            max[paxType] = otherType[paxType];
          }
        }
      }
      return max;
    },

    totals() {
      return this.roomTypes.length > 0
        ? {
            adults: Number(this.roomTotals.adults),
            children: Number(this.roomTotals.children),
            infants: Number(this.roomTotals.infants),
          }
        : {
            adults: Number(this.otherTotal.adults),
            children: Number(this.otherTotal.children),
            infants: Number(this.otherTotal.infants),
          };
    },
    otherTotal() {
      return this.otherTypesSettings.reduce(
        (t, otherType) => {
          t.adults = Number(t.adults) + Number(otherType.adults);
          t.children = Number(t.children) + Number(otherType.children);
          t.infants = Number(t.infants) + Number(otherType.infants);
          return t;
        },
        { adults: 0, children: 0, infants: 0 }
      );
    },
  },

  watch: {
    destinationId(val) {
      this.destinationIdFilter = val;
    },
    selectedTemplate(val) {
      this.template = { ...val };
    },
    dateRangeStart(val) {
      //this.startDate = val;
    },
    initialStartDate(val) {
      //this.startDate = val;
    },
  },

  methods: {
    onAddParticipant(payload) {
      const participant = payload
        ? { ...payload }
        : {
            title: "Mr.",
            name: "",
            surname: "",
            email: "",
            mobile: "",
          };
      this.participants.push(participant);
      // The create was blocked by the missing lead pax, so carry on with it now.
      if (this.pendingCreateItinerary) {
        this.pendingCreateItinerary = false;
        this.$refs.participantsList.showAddPaxDialog = false;
        this.$nextTick(() => this.onCreateItinerary());
      }
    },
    onReplaceParticipant(payload) {
      if (payload.index > -1) {
        Object.assign(this.participants[payload.index], { ...payload.new });
      }
    },
    onRemoveParticipant($payload) {
      this.participants.splice($payload, 1);
    },
    departureDates(date) {
      let result = false;
      const rangeStart = moment(this.dateRangeStart);
      const rangeEnd = moment(this.dateRangeEnd);
      if (
        this.templateDetails &&
        this.templateDetails.departures.edges.length > 0
      ) {
        for (const dep of this.templateDetails.departures.edges) {
          const depRangeStart = moment(dep.node.validFrom);
          const depRangeEnd = moment(dep.node.validTo);
          if (depRangeStart.isValid() && depRangeEnd.isValid()) {
            const d = moment(date);
            if (rangeStart.isValid() && rangeEnd.isValid()) {
              //check if it is in date range limit (add to itinerary context)
              if (
                d.isBetween(rangeStart, rangeEnd, "days", "[]") &&
                d.isBetween(depRangeStart, depRangeEnd, "days", "[]") &&
                dep.node.daysOfWeek.split(",").indexOf(d.day().toString()) > -1
              ) {
                result = true;
              }
            } else {
              // create new iti from template context
              if (
                d.isBetween(depRangeStart, depRangeEnd, "days", "[]") &&
                dep.node.daysOfWeek.split(",").indexOf(d.day().toString()) > -1
              ) {
                result = true;
              }
            }
          }
        }
      } else {
        //no departures
        if (rangeStart.isValid() && rangeEnd.isValid()) {
          //itinerary context
          result = moment(date).isBetween(rangeStart, rangeEnd, "days", "[]");
        } else {
          //new iti context
          result = true;
        }
      }
      return result;
    },
    onCancel() {
      this.$emit("onCancel");
    },
    onAdd() {
      const durationDiff = this.getDurationDiff();
      if (durationDiff < 0) {
        //show confirmation dialog
        this.showConfirmationDialog = true;
      } else {
        this.addTemplate();
      }
    },
    onCreateItinerary() {
      // An itinerary needs a lead passenger. Rather than leaving the button
      // dead, open the add pax dialog so it is clear what is missing.
      if (this.participants.length == 0) {
        this.pendingCreateItinerary = true;
        this.$refs.participantsList.onAdd();
        return;
      }
      this.pendingCreateItinerary = false;
      const payload = this.includeSettings
        ? {
            itineraryInput: this.getItineraryInput(),
            roomTypes: this.roomTypes.filter(
              (room) => room.adults > 0 || room.children > 0
            ),
          }
        : this.getItineraryInput();
      //console.log(payload);
      this.$emit("createItinerary", payload);
    },
    getItineraryInput() {
      return {
        adults: this.useMaxes
          ? this.maxes.adults.toString()
          : this.totals.adults.toString(),
        children: this.useMaxes
          ? this.maxes.children.toString()
          : this.totals.children.toString(),
        infants: this.useMaxes
          ? this.maxes.infants.toString()
          : this.totals.infants.toString(),
        department: this.templateDetails.department,
        destinationId: this.templateDetails.destination.id,
        brand: this.templateDetails.brand,
        internalStatus: this.templateDetails.internalStatus,
        agentId: this.$store.getters.agentId,
        startDate: moment(this.startDate).format("YYYY-MM-DD"),
        endDate: moment(this.startDate)
          //.startOf("day")
          .add(this.templateDetails.duration - 1, "days")
          .format("YYYY-MM-DD"),

        participants: this.participants.map(this.toItineraryParticipantInput),

        items: (() => {
          const allItems = this.getItineraryItems().sort(sortBy("order"));
          return allItems.map((item) => this.toItiItemInput(item, allItems));
        })(),
      };
    },
    toItineraryParticipantInput(item) {
      return {
        id: item.id ? item.id : undefined,
        title: item.title,
        name: item.name,
        surname: item.surname,
        email: item.email,
        mobile: item.mobile,
      };
    },
    toItiItemInput(item, allItems) {
      // Find parent item by matching parentUid to uid, then use parent's id as parentId
      let parentId = undefined;
      if (item.parentUid && allItems) {
        const parentItem = allItems.find((i) => i.uid === item.parentUid);
        if (parentItem) {
          parentId = parentItem.id;
        }
      }

      return {
        id: item.id,
        serviceId: item.serviceId,
        adults: item.adults,
        children: item.children,
        infants: item.infants,
        qty: item.qty,
        parentId: parentId,
        extras: item.extras ? [...item.extras] : undefined,
        startDate: item.startDate,
        endDate: item.endDate,
        boardBasis: item.basis,
        isHidden: item.isHidden,
        isIncluded: item.isIncluded,
        isOptional: item.isOptional,
        includeCommission: item.includeCommission,
        pricing: item.customPricing ? { ...item.pricing } : undefined,
        customPricing: item.customPricing,
        remarks: item.remarks
          ? { ...item.remarks }
          : {
              pickupPoint: "",
              pickupTime: "",
              dropoffPoint: "",
              dropoffTime: "",
            },
        source: this.templateDetails.isExternal
          ? { ...item.source }
          : undefined,
      };
    },
    onStartDateSelect($event) {
      this.startDate = $event;
    },
    onPaxChange(roomType) {
      const pax = Number(roomType.adults) + Number(roomType.children);
      roomType.qty = Math.ceil(pax / roomType.occupancy);
      roomType.min = Math.ceil(pax / roomType.occupancy);

      for (const ots of this.otherTypesSettings) {
        ots.adults = this.roomTotals.adults;
        ots.children = this.roomTotals.children;
        ots.infants = this.roomTotals.infants;
      }
    },
    getDurationDiff() {
      const start = moment(this.startDate);
      const end = moment(this.dateRangeEnd);
      //selected duration
      const duration = end.diff(start, "days") + 1;
      return duration - Number(this.templateDetails.duration);
    },
    onConfirm() {
      let addDays = -1 * this.getDurationDiff();
      this.$emit(
        "expandDateRange",
        moment(this.dateRangeEnd).add(addDays, "days").toISOString(true)
      );
      this.showConfirmationDialog = false;
      this.addTemplate();
    },
    addTemplate() {
      // #11320 — emit the picked template's id (chosen inside this dialog in the apply flow)
      // so the parent can copy its content onto the itinerary. The parent's selectedTemplate is
      // not set on this path, so the id must come from the dialog's own template.
      this.$emit(
        "addTemplate",
        this.getItineraryItems().sort(sortBy("order")),
        this.template && this.template.id
      );
    },
    getItineraryItems() {
      //   "statusId",
      //   "status"
      const selectedRooms = this.roomTypes.filter(
        (room) => room.adults > 0 || room.children > 0
      );

      return this.templateDetails.items.edges
        .map((node) => {
          //console.log(node);
          let itemDuration = Number(node.node.duration);
          const selectedStartDate = moment(this.startDate);
          const itemDay = Number(node.node.day) - 1;
          const itemStartDate = moment(selectedStartDate).add(itemDay, "days");

          let itemAdults = Number(this.maxAdults);
          let itemChildren = Number(this.maxChildren);
          let itemInfants = Number(this.maxChildren);
          let itemQty = Math.ceil(
            (Number(itemAdults) + Number(itemChildren)) /
              Number(node.node.service.stdOccupancy)
          );

          if (
            node.node.supplier.serviceType != "ACCOMMODATION" &&
            node.node.service.typeName != "Template Pricing" &&
            node.node.service.typeName != "Template Price"
          ) {
            //setup other types
            const otherTypeSetting = this.otherTypesSettings.find(
              (s) => s.type == node.node.supplier.serviceType
            );
            if (otherTypeSetting) {
              itemAdults = otherTypeSetting.adults;
              itemChildren = otherTypeSetting.children;
              itemInfants = otherTypeSetting.infants;
              itemQty = otherTypeSetting.infants;
              const pax = otherTypeSetting.adults + otherTypeSetting.children;
              itemQty = Math.ceil(pax / Number(node.node.service.stdOccupancy));
            }
            itemDuration--;
          } else {
            //handle accommodation
            if (selectedRooms.length > 0) {
              //console.log('selected rooms: ', selectedRooms);
              const roomSettings = selectedRooms.find(
                //(room) => room.occupancy == node.node.service.stdOccupancy
                //find by serviceTypeName
                (room) =>
                  this.serviceTypeBased &&
                  node.node.service.typeName != "Template Pricing" &&
                  node.node.service.typeName != "Template Price"
                    ? room.type == node.node.service.typeName
                    : room.occupancy == node.node.service.stdOccupancy
              );
              //console.log(roomSettings);
              if (roomSettings) {
                //setup room
                itemAdults = Number(roomSettings.adults);
                itemChildren = Number(roomSettings.children);
                itemInfants = Number(roomSettings.infants);
                itemQty = roomSettings.qty;
              } else {
                itemQty = 0;
              }
            }
            if (node.node.supplier.serviceType != "ACCOMMODATION") {
              itemDuration--; //https://tracker.globetrack.ie/issues/6893
            }
          }

          const itemEndDate = moment(itemStartDate).add(itemDuration, "days");
          const remarks = {
            pickupPoint: "",
            pickupTime: "",
            dropoffPoint: "",
            dropoffTime: "",
            agentNotes: "",
            supplierNotes: "",
            voucherNotes: "",
            internalNotes: "",
            confirmationNo: "",
            flightNo: "",
            roomNumbers: "",
          };
          if (node.node.service.serviceNotes.edges.length > 0) {
            //const filter by date range
            const filteredServiceNotes =
              node.node.service.serviceNotes.edges.filter((note) => {
                const noteStartDate = moment(note.node.dateFrom);
                const noteEndDate = moment(note.node.dateTo);
                return moment(itemStartDate).isBetween(
                  noteStartDate,
                  noteEndDate,
                  "days",
                  "[]"
                );
              });
            if (filteredServiceNotes.length > 0) {
              remarks.agentNotes = filteredServiceNotes
                .map((note) => note.node.description)
                .join("\n");
            }
          }
          return {
            id: randomize("Aa0", 32),
            uid: node.node.uid || randomize("Aa0", 32),
            parentUid: node.node.parentUid,
            status: "Quotation",
            adults: itemAdults,
            children: itemChildren,
            infants: itemInfants,
            qty: itemQty,
            order: Number(node.node.order),
            startDate: itemStartDate.toISOString(true),
            endDate: itemEndDate.toISOString(true),
            serviceName: node.node.service.name,
            supplierName: node.node.supplier.name,
            serviceType: node.node.supplier.serviceType,
            serviceId: node.node.service.id,
            price: node.node.customPricing ? node.node.totalSell : 0,
            isIncluded: node.node.included,
            isOptional: node.node.optional,
            //https://tracker.globetrack.ie/issues/7241
            includeCommission: true,
            isHidden: node.node.hidden,
            rateTypeCode: node.node.supplier.defaultRateTypeCode,
            selected: false,
            basis: node.node.supplier.boardBasis,
            customPricing: node.node.customPricing,
            source: {
              name: this.templateDetails.isExternal ? "GTWEB_MP" : "GTWEB",
              id: node.node.service.id,
            },
            remarks,
            pricing: node.node.customPricing
              ? {
                  adultBuy: node.node.adultBuy,
                  adultSell: node.node.adultSell,
                  childBuy: node.node.childBuy || 0,
                  childSell: node.node.childSell || 0,
                  infantBuy: node.node.infantBuy || 0,
                  infantSell: node.node.infantSell || 0,
                  itemBuy: node.node.itemBuy,
                  itemSell: node.node.itemSell,
                  rateTypeCode: node.node.rateTypeCode,
                  currencySell: node.node.currencySell,
                  currencyBuy: node.node.currencyBuy,
                }
              : undefined,
            extras:
              node.node.extras.edges.length > 0
                ? node.node.extras.edges.map(
                    this.mapToExtraItem({
                      serviceType: node.node.supplier.serviceType,
                      adults: itemAdults,
                      children: itemChildren,
                      infants: itemInfants,
                    })
                  )
                : undefined,
          };
        })
        .filter((item) => item.qty);
    },
    mapToExtraItem(item) {
      return (extra) => {
        const selectedStartDate = moment(this.startDate);
        const extraDay = Number(extra.node.day) - 1;
        const extraStartDate = moment(selectedStartDate).add(extraDay, "days");

        // const extraDuration =
        //   item.serviceType == "ACCOMMODATION"
        //     ? Number(extra.node.duration)
        //     : Number(extra.node.duration) - 1;
        const extraDuration = Number(extra.node.duration) - 1;

        const extraEndDate = moment(extraStartDate)
          .startOf("day")
          .add(extraDuration, "days");
        let qty = 1;
        if (extra.node.isChildOnly) {
          qty = item.children;
        } else if (extra.node.isInfantOnly) {
          qty = item.infants;
        } else {
          qty = Number(item.adults) + Number(item.children);
        }
        //console.log(item, qty, extra);

        return {
          id: extra.node.id,
          uid: extra.node.extraId,
          startDate: extraStartDate.toISOString(true),
          endDate: extraEndDate.toISOString(true),
          statusId: "U2VydmljZVN0YXR1cy0x",
          qty,
          isIncluded: extra.node.included,
          isOptional: extra.node.optional,
        };
      };
    },
    onTemplateSelect(event) {
      this.template = null; //reset template
      this.template = { ...event };
    },
    getRoomTypes() {
      const rt = [];
      if (this.templateDetails.items.edges.length > 0) {
        for (const item of this.templateDetails.items.edges) {
          if (
            rt.map((i) => i.type).indexOf(item.node.service.typeName) == -1 &&
            item.node.supplier.serviceType == "ACCOMMODATION"
          ) {
            const roomType = this.roomTypes.find(
              (r) => r.occupancy == Number(item.node.service.stdOccupancy)
            );
            rt.push({
              id: randomize("Aa0", 32),
              adults: roomType ? roomType.adults : 0,
              children: roomType ? roomType.children : 0,
              infants: roomType ? roomType.infants : 0,
              label: item.node.service.typeName,
              type: item.node.service.typeName,
              occupancy: Number(item.node.service.stdOccupancy),
              min: 0,
              qty: roomType ? roomType.qty : 0,
            });
          }
        }
      }
      return rt;
    },
  },
  apollo: {
    templateDetails: {
      query: TEMPLATE_DETAILS_QUERY,
      variables() {
        return {
          id: this.template.id,
          adults: Number(this.maxAdults),
          children: Number(this.maxChildren),
          infants: Number(this.maxInfants),
          agentId: this.agentId,
          department: this.department,
          isExternal: this.template.isExternal,
          pricingDate:
            this.startDate && this.startDate != "" ? this.startDate : undefined,
        };
      },
      result({ data }) {
        if (data.templateDetails) {
          if (!this.serviceTypeBased) {
            this.roomTypes = [...this.getRoomTypes()];
            this.serviceTypeBased = true;
          }
          //update startDate
          if (this.initialStartDate != "" && this.startDate == "") {
            this.startDate = this.initialStartDate;
          }
        }
      },
      skip() {
        return !this.template.hasOwnProperty("id");
      },
      error(err) {
        const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
        this.$store.dispatch("showAppSnackbar", {
          msg: errorMsg,
          color: "error",
        });
      },
      fetchPolicy: "network-only",
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/templates/TemplateSettingsDialog.vue